import { useState, useEffect, useRef } from 'react';
import { Shield, Play, CheckCircle, XCircle, Loader2, Globe, Layout, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

interface TestResult {
  id: string;
  name: string;
  lang: string;
  path: string;
  status: 'pending' | 'running' | 'pass' | 'fail' | 'error';
  error?: string;
  duration?: number;
}

const LANGUAGES = ['lv', 'en', 'de', 'ru', 'lt', 'et', 'es', 'pt', 'pl', 'no', 'sl', 'sv', 'ro', 'nl', 'it', 'fr', 'hr'];

const PAGES_TO_TEST = [
  { name: 'Landing Page', path: '/landing' },
  { name: 'Privacy Policy', path: '/privacy' },
  { name: 'Terms of Service', path: '/terms' },
  { name: 'Garage', path: '/garage' },
  { name: 'Settings', path: '/settings' },
  { name: 'Pricing Plans', path: '/settings/plans' },
  { name: 'Alternatives', path: '/alternatives' }
];

// Add a few car-specific pages if a car exists
const CAR_PAGES = [
  { name: 'Car Dashboard', path: '/:slug' },
  { name: 'Statistics', path: '/:slug/stats' },
  { name: 'History', path: '/:slug/expenses' },
  { name: 'Parts', path: '/:slug/parts' },
  { name: 'Notes', path: '/:slug/notes' },
  { name: 'Locations', path: '/:slug/locations' },
  { name: 'Export', path: '/:slug/export' }
];

export function AdminTests() {
  const { user } = useAuth();
  const [results, setResults] = useState<TestResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [currentTestIndex, setCurrentTestIndex] = useState(-1);
  const [demoCarSlug, setDemoCarSlug] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const testStartTime = useRef<number>(0);

  // Initialize test list
  useEffect(() => {
    const fetchDemoCar = async () => {
      try {
        const { data } = await supabase.from('cars').select('slug').limit(1).maybeSingle();
        if (data?.slug) {
          setDemoCarSlug(data.slug);
        }
      } catch (e) {
        console.error('Failed to fetch demo car:', e);
      }
    };
    fetchDemoCar();

    const allTests: TestResult[] = [];
    
    // Combine regular pages and car pages
    const pages = [...PAGES_TO_TEST, ...CAR_PAGES];
    
    pages.forEach(page => {
      LANGUAGES.forEach(lang => {
        allTests.push({
          id: `${page.path}-${lang}`,
          name: page.name,
          lang,
          path: page.path,
          status: 'pending'
        });
      });
    });

    setResults(allTests);
  }, []);

  const startTests = () => {
    if (isRunning) return;
    
    // Reset all results to pending
    setResults(prev => prev.map(r => ({ ...r, status: 'pending', error: undefined, duration: undefined })));
    setIsRunning(true);
    setCurrentTestIndex(0);
  };

  const stopTests = () => {
    setIsRunning(false);
    setCurrentTestIndex(-1);
  };

  // The Test Runner Effect
  useEffect(() => {
    if (!isRunning || currentTestIndex < 0 || currentTestIndex >= results.length) {
      if (isRunning && currentTestIndex >= results.length) {
        setIsRunning(false);
      }
      return;
    }

    const currentTest = results[currentTestIndex];
    if (currentTest.status !== 'pending') return;

    // Update status to running
    setResults(prev => {
      const next = [...prev];
      next[currentTestIndex] = { ...next[currentTestIndex], status: 'running' };
      return next;
    });

    testStartTime.current = Date.now();

    // Construct URL
    // We append test_mode=true and lang=XX and disable heavy things if needed
    const baseUrl = window.location.origin;
    let targetPath = currentTest.path;
    
    // Replace slug if needed
    if (targetPath.includes(':slug')) {
       if (demoCarSlug) {
         targetPath = targetPath.replace(':slug', demoCarSlug);
       } else {
         // Skip car test if no slug
         setResults(prev => {
           const next = [...prev];
           next[currentTestIndex] = { ...next[currentTestIndex], status: 'pass', error: 'Skipped: No car slug' };
           return next;
         });
         setCurrentTestIndex(prev => prev + 1);
         return;
       }
    }

    const testUrl = `${baseUrl}${targetPath}${targetPath.includes('?') ? '&' : '?'}lang=${currentTest.lang}&test_mode=true&t=${Date.now()}`;
    
    if (iframeRef.current) {
      iframeRef.current.src = testUrl;
    }

    // Timeout for each test (e.g. 10 seconds)
    const timeoutId = setTimeout(() => {
      handleTestFinished('error', 'Timeout (10s)');
    }, 10000);

    return () => clearTimeout(timeoutId);
  }, [isRunning, currentTestIndex, demoCarSlug]);

  // Listen for messages from the test iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      
      if (event.data?.type === 'TEST_LOADED') {
        handleTestFinished('pass');
      } else if (event.data?.type === 'TEST_ERROR') {
        handleTestFinished('fail', event.data.message || 'Unknown error');
      }
    };

    window.addEventListener('message', handleMessage);
    
    // Also monitor iframe console errors if possible (same origin allowed)
    const interval = setInterval(() => {
      if (!isRunning || currentTestIndex < 0) return;
      
      try {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
           // We can't easily sync console logs unless we inject script
           // But we can check if the body has a common 'crash' signature or is empty
           const innerDoc = iframe.contentDocument || iframe.contentWindow.document;
           if (innerDoc.body && innerDoc.body.innerText.includes('Something went wrong') || innerDoc.body.innerText.includes('App Crash')) {
              handleTestFinished('fail', 'React Error Boundary Triggered');
           }
        }
      } catch (e) {
        // Cross-origin issues (shouldn't happen on same origin)
      }
    }, 1000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(interval);
    };
  }, [isRunning, currentTestIndex]);

  const handleTestFinished = (status: TestResult['status'], error?: string) => {
    if (!isRunning || currentTestIndex < 0) return;

    const duration = Date.now() - testStartTime.current;

    setResults(prev => {
      const next = [...prev];
      if (next[currentTestIndex]) {
        next[currentTestIndex] = { 
          ...next[currentTestIndex], 
          status, 
          error,
          duration 
        };
      }
      return next;
    });

    // Move to next test
    setCurrentTestIndex(prev => prev + 1);
  };

  const stats = {
    total: results.length,
    passed: results.filter(r => r.status === 'pass').length,
    failed: results.filter(r => r.status === 'fail' || r.status === 'error').length,
    pending: results.filter(r => r.status === 'pending').length,
    running: results.filter(r => r.status === 'running').length,
  };

  return (
    <div className="space-y-6 font-primary text-slate-200">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-white flex items-center gap-3">
            <Shield className="text-blue-500" />
            System Reliability Tests
          </h1>
          <p className="text-slate-400 mt-1">Automatic UI smoke tests across all languages and views.</p>
        </div>

        <div className="flex gap-3">
          {!isRunning ? (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  throw new Error("Sentry Test Error from Admin Console");
                }}
                className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-400 px-4 py-3 rounded-2xl font-bold transition-all border border-slate-700"
                title="Sūtīt testa kļūdu uz Sentry"
              >
                <AlertTriangle size={18} />
                Test Sentry
              </button>
              <button
                onClick={startTests}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-6 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-blue-500/20"
              >
                <Play size={20} />
                Run All Tests
              </button>
            </div>
          ) : (
            <button
              onClick={stopTests}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-6 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-red-500/20"
            >
              <Loader2 size={20} className="animate-spin" />
              Stop Testing
            </button>
          )}
        </div>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Tests" value={stats.total} icon={Layout} color="blue" />
        <StatCard label="Passed" value={stats.passed} icon={CheckCircle} color="green" />
        <StatCard label="Failed" value={stats.failed} icon={XCircle} color="red" />
        <StatCard label="Progress" value={`${Math.round(((stats.total - stats.pending) / stats.total) * 100) || 0}%`} icon={Loader2} color="slate" spin={isRunning} />
      </div>

      {/* Test List */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-900 border-b border-slate-800">
                <th className="px-6 py-4 font-black text-slate-400 text-xs uppercase tracking-wider">Page / View</th>
                <th className="px-6 py-4 font-black text-slate-400 text-xs uppercase tracking-wider">Language</th>
                <th className="px-6 py-4 font-black text-slate-400 text-xs uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 font-black text-slate-400 text-xs uppercase tracking-wider">Duration</th>
                <th className="px-6 py-4 font-black text-slate-400 text-xs uppercase tracking-wider">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {results.filter(r => r.status !== 'pending' || isRunning).map((result) => (
                <tr key={result.id} className={`${result.status === 'running' ? 'bg-blue-500/5' : ''} transition-colors`}>
                  <td className="px-6 py-4">
                    <div className="font-bold text-white">{result.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{result.path}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Globe size={14} className="text-slate-500" />
                      <span className="font-bold uppercase text-slate-300">{result.lang}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={result.status} />
                  </td>
                  <td className="px-6 py-4 text-slate-500 font-mono text-xs">
                    {result.duration ? `${result.duration}ms` : '--'}
                  </td>
                  <td className="px-6 py-4">
                    {result.error && (
                      <div className="flex items-center gap-2 text-red-400 text-xs font-semibold">
                        <AlertTriangle size={14} />
                        {result.error}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {results.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500 italic">
                    Push "Run All Tests" to start systemic verification.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hidden Test Iframe */}
      <div className="fixed bottom-4 right-4 w-64 h-36 bg-black rounded-xl border border-slate-700 shadow-2xl overflow-hidden opacity-20 pointer-events-none hover:opacity-100 hover:pointer-events-auto transition-opacity z-[9999]">
        <div className="bg-slate-800 px-2 py-1 text-[10px] font-black uppercase flex justify-between">
          <span>Test Runner Window</span>
          {isRunning && <span className="text-blue-400 animate-pulse">Live</span>}
        </div>
        <iframe
          ref={iframeRef}
          className="w-full h-full border-none bg-white"
          title="Smoke Test Runner"
          sandbox="allow-same-origin allow-scripts allow-forms"
        />
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, spin }: any) {
  const colors: any = {
    blue: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
    green: 'text-green-500 bg-green-500/10 border-green-500/20',
    red: 'text-red-500 bg-red-500/10 border-red-500/20',
    slate: 'text-slate-400 bg-slate-400/10 border-slate-400/20'
  };

  return (
    <div className={`p-4 rounded-3xl border ${colors[color]} flex items-center gap-4`}>
      <div className={`p-3 rounded-2xl bg-white/5`}>
        <Icon size={24} className={spin ? 'animate-spin' : ''} />
      </div>
      <div>
        <div className="text-2xl font-black text-white">{value}</div>
        <div className="text-xs font-bold opacity-60 uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: TestResult['status'] }) {
  switch (status) {
    case 'pass':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/10 text-green-500 text-xs font-black uppercase">
          <CheckCircle size={12} /> Pass
        </span>
      );
    case 'fail':
    case 'error':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 text-red-500 text-xs font-black uppercase">
          <XCircle size={12} /> Fail
        </span>
      );
    case 'running':
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 text-blue-500 text-xs font-black uppercase">
          <Loader2 size={12} className="animate-spin" /> Running
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-500/10 text-slate-500 text-xs font-black uppercase tracking-tight">
          Pending
        </span>
      );
  }
}
